<template>

  <LoadingDiv v-if="!isReady" />

  <div class="auth-enrollee-shell sba-slide-in" v-else>
    <div class="auth-enrollee-workbench" v-if="encounter">
      <div class="section auth-enrollee-section">
        <!-- <br /> -->
        <div class="card z-depth-0 auth-enrollee-card">
          <div class="card-content auth-enrollee-dossier">
            <div class="auth-enrollee-dossier-grid">
              <div class="auth-enrollee-identity">
                <div class="auth-enrollee-kicker">
                  <span>{{ encounter.scheme.name.toUpperCase() }}</span>
                  <span>{{ encounter.scheme.type == 'p' ? 'Private Scheme' : 'Social Scheme' }}</span>
                </div>
                <p>{{ encounter.client_name }}</p>
                <h5>
                  {{ encounter.name }}
                  <sup><small><span class="black-text">{{
                    showAgeSex(encounter.dob, encounter.sex)
                        }}</span></small></sup>
                </h5>
                <div class="auth-enrollee-meta">
                  <span>{{ encounter.enrollee_number }}</span>
                  <span>{{ showAgeSex(encounter.dob, encounter.sex) }}</span>
                </div>
              </div>
              <div class="auth-enrollee-avatar">
                <img :src="encounter.profile_img" width="96" class="circle responsive-img right"
                  v-if="encounter.profile_img" />
                <img :src="`https://ui-avatars.com/api/?name=${encounter.name}&size=128`" width="96"
                  class="circle responsive-img right" v-else />
              </div>
            </div>
          </div>

          <!-- Entire Provider (diagnosis, claims) Display starts here -->
          <div class="card auth-provider-workflow">
            <!-- Provider Name -->
            <div class="card-content auth-provider-summary" v-if="hasSelectedProvider">
              <div class="auth-provider-summary-grid">
                <div class="auth-provider-summary-icon">
                  <i class="material-icons">local_hospital</i>
                </div>
                <div>
                  <span>Selected Provider</span>
                  <h6 v-if="selectedProvider && selectedProvider.name">{{ selectedProvider.name }}</h6>
                  <p>
                    {{
                      new Date(serverDate).toLocaleString(
                        undefined,
                        {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          weekday: 'long',
                        }
                      )
                    }}
                  </p>
                </div>
                <a href="#" class="auth-provider-clear" title="clear" @click.prevent="handleRestart">
                  <i class="material-icons">close</i>
                </a>
              </div>
            </div>

            <!-- Provider Form -->
            <div class="card-panel auth-provider-panel" v-if="!hasSelectedProvider">
              <div class="auth-provider-grid">
                <div class="auth-provider-copy">
                  <span>Step 2</span>
                  <h6>Provider</h6>
                  <p>Select the provider handling this authorization request.</p>
                </div>
                <div class="auth-provider-picker">
                  <div class="input-field">
                    <FilterSimpleDiv inputId="filterProvider" fieldName="Select Provider" :data="filterProviderData"
                      :search="true" :appendDropdownToBody="true" :forceDropdownDown="true"
                      dropdownClass="auth-provider-autocomplete-dropdown"
                      @selected="handlePick" @searching="handleSearch" />
                  </div>
                </div>
                <div class="auth-provider-actions">
                    <button class="waves-effect waves-light btn-large black auth-provider-action" :class="{
                      disabled: isLoading || (!selectedProvider || (!siloMode && !selectedProvider.opid)),
                    }" @click="handleProvider()">
                      <i class="material-icons left" v-if="!isLoading">chevron_right</i>{{ isLoading ? "Going..." :
                        "Auth" }}
                    </button>
                    <button class="waves-effect waves-light btn-large modal-trigger auth-provider-action auth-provider-send" :class="{
                      disabled: isLoading || (!selectedProvider || (!siloMode && !selectedProvider.opid)),
                    }" data-target="confirmSubmitEncounterOnly">
                      <i class="material-icons left" v-if="!isLoading">send</i>{{ isLoading ? "Submitting..." :
                        "Send" }}
                    </button>
                </div>
                <div class="auth-provider-warning">
                  <p
                    v-if="!siloMode && (selectedProvider?.name && !selectedProvider?.opid)">
                    <i class="material-icons">error_outline</i>
                    {{ selectedProvider?.name }} is unable to receive authorization.
                    Provider is in-active!
                  </p>
                </div>
              </div>
            </div>

            <!-- New -->
            <template v-if="hasSelectedProvider">
              <!-- Display Diagnosis -->
              <div class="auth-diagnosis-row" v-if="diagnosisList.length">
                  <div class="auth-diagnosis-card">
                    <i class="material-icons">bookmark_border</i>
                    <div>
                      <span>Diagnosis</span>
                      <p>{{ diagnosisList.join(", ") }}</p>
                    </div>
                    <a href="#" class="edit-black-item" title="edit diagnosis" @click.prevent="handleReviewDiagnosis"><i class="material-icons" :class="{
                          'black-text': !resetDiagnosis,
                          'blue-text': resetDiagnosis,
                        }">edit</i></a>
                  </div>
              </div>

              <!-- Claims Display Card -->
              <div class="card auth-claims-card" v-if="claimsDisplayCard">
                <div class="card-content">
                  <span class="card-title">Item(s)</span>

                  <!-- List claims -->
                  <ol id="item-list" class="collection">
                    <li class="collection-item auth-line-item" v-for="claim in claims" :key="claim.id">
                      <span class="auth-line-copy">
                        <b>{{ claim.claim }}</b> x {{ claim.qty }} (₦{{
                          claim.amt
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        }})
                      </span>

                      <span class="line-item-actions">
                        <a href="#" class="secondary-content cancel-item" @click.prevent="handleDelete(claim)"
                          title="delete"><i class="material-icons grey-text">cancel</i></a>

                        <a href="#" class="secondary-content edit-item" @click.prevent="handleEdit(claim)" title="edit"><i
                            class="material-icons" :class="{
                              'grey-text': editId != claim.id,
                              'blue-text': editId == claim.id,
                            }">edit</i></a>
                      </span>
                    </li>
                  </ol>

                  <!-- Claims Submit -->
                  <div v-if="claimsSubmitCard">
                    <!-- Total -->
                    <ul id="item-list" class="collection">
                      <li class="collection-item pull-right">
                        <b>Total</b> (Sent Amount):
                        <b><span class="red-text">₦{{ totalSent(claimsList) }}</span></b>
                      </li>
                    </ul>

                    <div v-if="error">
                      <p class="auth-claims-error">
                        <i class="material-icons">error_outline</i>
                        {{ error }}
                      </p>
                    </div>

                    <!-- Add -->
                    <div class="auth-claims-submit-strip">
                      <button class="waves-effect waves-light btn-large black" :class="{ disabled: isSubmitting }"
                        @click.prevent="handleNext('moreClaims')"><i
                          class="medium material-icons left">add</i>Add Item</button>

                      <!-- Submit -->
                      <button class="waves-effect waves-light btn-large green modal-trigger" :class="{
                        disabled: isSubmitting,
                      }" data-target="confirmSubmit"><i class="large material-icons right">check_circle</i>{{
                        isSubmitting ? "Authorizing ..." : "Authorize" }}
                      </button>
                    </div>

                  </div>
                </div>
              </div>

              <!-- Sumbmitting -->
              <div class="progress auth-submit-progress" v-if="isSubmitting">
                <div class="indeterminate"></div>
              </div>

              <!-- Add claim -->
              <AddClaim :turl="{ sid: encounter?.scheme_id, pid: encounter?.provider_id }" :type="type" :next="next"
                :editDiagnosis="resetDiagnosis" :claimItem="claim" @update="handleUpdate" @clear="handleClear"
                v-if="openAuth" />
            </template>

            <!-- History -->
            <div class="auth-history-shell">
              <HistoryAuth
                :enrolleeNumber="encounter.enrollee_number"
                :principal-id="encounter.principal_id"
                :scheme-id="encounter.scheme_id || encounter.scheme?.id"
                :hideUnFound="hideUnFound"
              />
            </div>
          </div>
        </div>
      </div>

      <Teleport to="body">
        <Modal id="confirmSubmitEncounterOnly" class="modal auth-confirm-modal" :title="alertTitle"
          :message="alertMessageEncounterOnly" @confirm="handleConfirmEncounterOnly" />
        <Modal id="confirmSubmit" class="modal auth-confirm-modal" :title="alertTitle" :message="alertMessage"
          @confirm="handleConfirm" />
      </Teleport>

    </div>

    <div class="section center auth-no-encounter" v-if="!encounter">
      <h6>No Encounter Record for Enrollee <em>found</em>...</h6>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUpdated, computed, watchEffect, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { ageSex } from "@/composables/fxLibrary/coreFx";
import { OPID } from '@/laravel/config';
import getItems from "@/composables/getItems";
import {
  debounce,
  loadCachedProviderFilter,
} from "@/composables/requestOptimization";
import postItemRemote from "@/composables/oneProvider/postItem";
import postItem from "@/composables/postItem";
import { beginAdminAuthorizationRequest } from "@/composables/authorizationRealtimeCoordinator";
import LoadingDiv from "@/components/LoadingDiv.vue";
import AddClaim from "@/components/claim/AddClaimComponent.vue";
import HistoryAuth from "@/components/auth/HistoryAuthComponent.vue";
import FilterSimpleDiv from "@/components/search/FilterSimpleDiv.vue";
import Modal from "@/components/alerts/ModalComponent.vue";
import { siloMode } from '@/laravel/config';
import M from "materialize-css";


export default {
  name: "EnrolleeAuthComponent",
  props: ["enrollee", "serverDate"],
  emits: ["restart"],
  components: { LoadingDiv, FilterSimpleDiv, AddClaim, HistoryAuth, Modal },
  setup(props, context) {
    const response = ref(null);
    const responseSearch = ref(null);
    const selectedItem = ref(null);
    const selectedProvider = ref(null);
    const isReady = ref(true);
    const isLoading = ref(false);
    const isSubmitting = ref(false);
    const alertTitle = ref("Confirm");
    const alertMessage = ref("Are you sure you want to approve? Please confirm that all the details provided are accurate. Once submitted, changes cannot be made.");
    const alertMessageEncounterOnly = ref("You are registering enrollee encounter without authorization. Click confirm to proceed!");
    const error = ref(false);
    const editId = ref(null);
    const editClaim = ref(null);
    const deleteClaim = ref(null);
    const claimsDisplayCard = ref(false);
    const claimsSubmitCard = ref(false);
    const diagnosisList = ref([]);
    const resetDiagnosis = ref(false);
    const type = ref("auth"); // close claim after input
    const next = ref("next");
    const hideUnFound = ref(true);
    const claimsList = ref([]);
    const claim = ref(null);
    const openAuth = ref(true);
    const postResource = ref(null);
    const endPoint = ref(null);
    let providerFilterRequestId = 0;

    // router
    const router = useRouter();

    // store
    const store = useStore();

    // sort silo
    onMounted(() => {
      // sort
      if (!siloMode) {
        postResource.value = postItemRemote;
        endPoint.value = `remote/claim/encounter/bulk`;
      } else {
        postResource.value = postItem;
        endPoint.value = `claim/encounter/bulk`;
      }
    });

    // Provider
    const filterProviderData = computed(() => {
      // start
      let val = null;

      let valPlus;

      if (responseSearch.value) {
        if (responseSearch.value.key == "filterProvider") {
          valPlus = responseSearch.value.value.reduce((newArr, x) => {
            newArr[x.name] = null;
            return newArr;
          }, {});
          val = { ...val, ...valPlus };
        }
      }

      // return final
      return val;
    });

    const loadProviderFilterSearch = debounce(async (query, requestId) => {
      await loadCachedProviderFilter({
        getItems,
        query,
        requestId,
        currentRequestId: () => providerFilterRequestId,
        onResult: (payload) => {
          responseSearch.value = payload;
        },
      });
    });

    // add up
    const totalSent = (claims) => {
      let total = 0;
      claims.forEach((x) => {
        total = Number(total) + Number(x.amt);
      });
      return total
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };

    // ageSex
    const showAgeSex = (dob, sex) => {
      const { fx } = ageSex(dob, sex);
      return fx.value;
    };

    // starter
    onMounted(() => {
      // get started
      response.value = props.enrollee;
    });

    // alert
    onMounted(() => {
      // modal
      const elems = document.querySelectorAll(".modal");
      M.Modal.init(elems, {
        endingTop: "24%",
        startingTop: "18%",
      });
    });

    // alert
    onUpdated(() => {
      // modal
      const elems = document.querySelectorAll(".modal");
      M.Modal.init(elems, {
        endingTop: "24%",
        startingTop: "18%",
      });
    });

    // filter components where search is set to "true"
    const handleSearch = (val) => {
      if (val.id == "filterProvider") {
        providerFilterRequestId++;
        loadProviderFilterSearch(val.value, providerFilterRequestId);
      }
    };

    // get option
    const handlePick = (val) => {
      selectedItem.value = val;
      // watchEffect will take over from here
    };

    // search watcher
    watchEffect(() => {
      // start
      if (selectedItem.value) {
        // sort
        if (selectedItem.value.id == "filterProvider") {
          //set
          if (!selectedItem.value.value) {
            selectedProvider.value = null;
          } else {
            responseSearch.value.value.forEach((x) => {
              if (x.name == selectedItem.value.value) {
                // found
                selectedProvider.value = { name: x.name, search: x.id, opid: x.opid };
              }
            });
          }
        }
      }
    });

    // scrollbar
    const resetScrollBar = () => {
      // Restore scrollbar (insurance)
      document.body.style.overflow = 'auto';
    };

    const scrollToAuthSection = (selector) => {
      nextTick(() => {
        window.setTimeout(() => {
          const el = document.querySelector(selector);
          if (!el) {
            return;
          }

          const headerOffset = 140;
          const top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: Math.max(top, 0),
            behavior: "smooth",
          });
        }, 80);
      });
    };

    // handle provider
    const handleProvider = () => {
      if (!canUseSelectedProvider.value) {
        return;
      }

      // open auth
      openAuth.value = true; // insurance
      // Set provider
      response.value.provider_id = selectedProvider.value.search;
    };

    // handle Emitted update
    const handleUpdate = (val) => {
      if (!hasSelectedProvider.value) {
        return;
      }

      const isReturningToItemsCard = !claimsSubmitCard.value && val.claimsSubmitCard;

      claimsDisplayCard.value = val.claimsDisplayCard;
      claimsSubmitCard.value = val.claimsSubmitCard;
      claimsList.value = [...val.claimsList];
      diagnosisList.value = [...val.diagnosisList];

      if (isReturningToItemsCard) {
        scrollToAuthSection(".auth-claims-card");
      }
    };

    // handle edit
    const handleEdit = (val) => {
      if (!hasSelectedProvider.value) {
        return;
      }

      claim.value = { action: "edit", claim: val };
      editId.value = val.id;
    };

    // handle edit
    const handleDelete = (val) => {
      if (!hasSelectedProvider.value) {
        return;
      }

      claim.value = { action: "delete", claim: val };
    };

    // handle remove (passed as props)
    const handleReviewDiagnosis = () => {
      if (!hasSelectedProvider.value) {
        return;
      }

      resetDiagnosis.value = true;
    };

    // handle next
    const handleNext = (val) => {
      if (!hasSelectedProvider.value) {
        return;
      }

      next.value = val;

      if (val === "moreClaims") {
        scrollToAuthSection(".claim-add-card");
      }
    };

    // handle clear (emiited)
    const handleClear = (val) => {
      if (val.id == "resetDiagnosis") {
        resetDiagnosis.value = val.value;
      } else if (val.id == "claim") {
        claim.value = val.value;
      } else if (val.id == "next") {
        next.value = val.value;
      } else if (val.id == "editId") {
        editId.value = val.value;
      }
    };

    // hanlde restart
    const handleRestart = () => {
      selectedProvider.value = null;
      selectedItem.value = null;
      claimsDisplayCard.value = false;
      claimsSubmitCard.value = false;
      claimsList.value = [];
      diagnosisList.value = [];
      resetDiagnosis.value = false;
      claim.value = null;
      editId.value = null;

      if (response.value?.provider_id) {
        delete response.value.provider_id;
      }

      context.emit("restart", null);
    };

    // confirmation
    const handleConfirm = (val) => {
      // call submit if confirmed
      if (val) {
        handleSubmit();
      }
    }

    // confirmation
    const handleConfirmEncounterOnly = (val) => {
      // call submit if confirmed
      if (val && canUseSelectedProvider.value) {
        // close auth
        openAuth.value = false;
        // Set provider
        response.value.provider_id = selectedProvider.value.search;
        // submit
        handleSubmit();
      }
    }

    // handle final submit
    const handleSubmit = async () => {
      if (!hasSelectedProvider.value) {
        error.value = "Select a provider before adding diagnosis or authorization items.";
        resetScrollBar();
        return;
      }

      // clear error
      error.value = false;

      // start
      isSubmitting.value = true;

      // add encounter date
      response.value.encounter_date = props.serverDate; // (goes into encounter)

      // setup encounter
      let diagnosis = diagnosisList.value?.join(", ");
      let keysToRemove = ["id", "scheme", "created_at", "updated_at"];
      let newEncounter = {
        ...Object.fromEntries(
          Object.entries(encounter.value).filter(
            ([key]) => !keysToRemove.includes(key)
          )
        ),
      };
      newEncounter = {
        ...newEncounter,
        encounter_cluster_id: null,
        diagnosis: diagnosis,
        user_id: siloMode ? store.state.user?.id : 0, // from easyHMO
      };

      // setup claims
      let newClaims = [];

      claims.value.forEach((x) => {
        newClaims.push({
          encounter_id: null,
          item: x.claim,
          sent_qty: x.qty,
          sent_price: x.amt,
          approved_qty: null,
          approved_price: null,
          standard_price: x.standard,
          authorization: "z",
          authorization_code: null,
          status: "a",
        });
      });

      // put all together
      let newVal = { encounter: newEncounter, claims: newClaims };
      // set final endPoint
      let finalEndPoint = siloMode ? endPoint.value : `${endPoint.value}/${OPID}/${selectedProvider.value?.opid}`;

      // post item
      const requestEcho = beginAdminAuthorizationRequest({ store, encounter: newEncounter, itemCount: newClaims.length, existing: false });
      const { res } = await postItem(finalEndPoint, newVal);

      if (res.value) {

        if (res.value.status) {
          requestEcho.finish(res.value.data);

          // final end
          router.push({
            name: "auth",
            query: {
              encounterId: res.value.data.id,
              created: 1,
            },
          });

        } else {
          requestEcho.cancel();
          // claims month has closed
          error.value = "Bills for enrollee Month has been submitted!";
          isSubmitting.value = false; // end

          // reset on fail
          resetScrollBar();
        }

      } else {
        requestEcho.cancel();

        // manage errors
        error.value = "Sorry, something went wrong!";
        isSubmitting.value = false; // end

        // reset on fail
        resetScrollBar();

      }
    };

    // output encounter results
    const encounter = computed(() => {
      if (!response.value) {
        return null;
      } else {
        return response.value;
      }
    });

    const hasSelectedProvider = computed(() => {
      return Boolean(response.value?.provider_id && canUseSelectedProvider.value);
    });

    const canUseSelectedProvider = computed(() => {
      return Boolean(selectedProvider.value?.search && (siloMode || selectedProvider.value?.opid));
    });

    // output claims results
    const claims = computed(() => {
      if (!claimsList.value) {
        return [];
      } else {
        return claimsList.value;
      }
    });

    return {
      showAgeSex,
      handleProvider,
      handleEdit,
      handleReviewDiagnosis,
      handleDelete,
      handleUpdate,
      handleNext,
      handleRestart,
      handleConfirm,
      handleConfirmEncounterOnly,
      handleSubmit,
      handleClear,
      handleSearch,
      handlePick,
      alertTitle,
      alertMessage,
      alertMessageEncounterOnly,
      selectedItem,
      filterProviderData,
      selectedProvider,
      hasSelectedProvider,
      totalSent,
      isReady,
      isLoading,
      isSubmitting,
      error,
      editId,
      claimsDisplayCard,
      claimsSubmitCard,
      resetDiagnosis,
      diagnosisList,
      editClaim,
      deleteClaim,
      encounter,
      claimsList,
      hideUnFound,
      next,
      type,
      claim,
      claims,
      openAuth,
      siloMode: siloMode,
    };
  },
};
</script>

<style scoped>
.auth-enrollee-shell {
  margin-top: 16px;
  width: 100%;
}

.auth-enrollee-workbench {
  width: 100%;
}

.auth-enrollee-card,
.auth-provider-workflow,
.auth-provider-summary,
.auth-provider-panel,
.auth-diagnosis-card,
.auth-claims-card,
.auth-history-shell,
.auth-no-encounter {
  animation: sbaSlideIn var(--sba-slide-duration, 260ms) ease-out both;
}

.auth-enrollee-section {
  margin: 0;
  padding: 0;
  width: 100%;
}

.auth-enrollee-card,
.auth-provider-workflow {
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: none;
  overflow: visible;
  width: 100%;
}

.auth-enrollee-card {
  box-shadow: none;
  margin: 0;
}

.auth-enrollee-dossier {
  background:
    linear-gradient(90deg, rgba(0, 137, 123, 0.08), rgba(255, 255, 255, 0) 38%),
    #ffffff;
  border-bottom: 1px solid #c4d3d9;
  padding: 28px 30px !important;
  position: relative;
}

.auth-enrollee-dossier-grid {
  align-items: center;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 104px;
  gap: 22px;
  margin-bottom: 0;
}

.auth-enrollee-identity {
  min-width: 0;
  position: relative;
  z-index: 1;
}

.auth-enrollee-kicker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.auth-enrollee-kicker span {
  background: #eef5f6;
  border: 1px solid #d2e2e6;
  border-radius: 999px;
  color: #455a64;
  display: inline-flex;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  padding: 7px 10px;
  text-transform: uppercase;
}

.auth-enrollee-avatar {
  display: flex;
  justify-content: flex-end;
  min-width: 86px;
}

.auth-enrollee-dossier p {
  color: #607d8b;
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 8px;
}

.auth-enrollee-dossier h5 {
  color: #263238;
  font-size: 1.52rem;
  font-weight: 800;
  line-height: 1.18;
  margin: 0;
  max-width: 720px;
  overflow-wrap: anywhere;
}

.auth-enrollee-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
}

.auth-enrollee-meta span {
  background: #ffffff;
  border: 1px solid #dbe5e9;
  border-radius: 6px;
  color: #455a64;
  display: inline-flex;
  font-weight: 750;
  line-height: 1;
  padding: 9px 11px;
}

.auth-enrollee-dossier img {
  display: block;
  height: 96px;
  object-fit: cover;
  opacity: 0.9;
  width: 96px;
}

.auth-provider-workflow {
  border: 0;
  border-radius: 0;
  margin: 0;
  padding: 18px 16px 0;
}

.auth-provider-summary {
  background: #ffffff !important;
  background-color: #ffffff !important;
  border: 1px solid #dbe5e9;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  box-shadow: none;
  box-sizing: border-box;
  margin: 0;
  padding: 18px 22px !important;
  width: 100%;
}

.auth-provider-summary-grid {
  align-items: center;
  display: grid;
  gap: 18px;
  grid-template-columns: 52px minmax(0, 1fr) 42px;
  min-height: 64px;
}

.auth-provider-summary-icon {
  align-items: center;
  background: #e8f3f2;
  border: 1px solid #cde8e5;
  border-radius: 6px;
  color: #00897b;
  display: inline-flex;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.auth-provider-summary-grid span {
  color: #607d8b;
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 7px;
  text-transform: uppercase;
}

.auth-provider-summary-grid h6 {
  color: #263238 !important;
  font-size: 1.08rem;
  font-weight: 600;
  line-height: 1.25;
  margin: 0;
  overflow-wrap: anywhere;
}

.auth-provider-summary-grid p {
  color: #00897b;
  font-weight: 550;
  margin: 6px 0 0;
}

.auth-provider-clear {
  align-self: center;
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe5e9;
  border-radius: 6px;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.auth-provider-clear i {
  color: #ef6c00;
}

.auth-provider-panel {
  background: #ffffff !important;
  border: 0;
  border-bottom: 1px solid #c4d3d9;
  border-radius: 0;
  box-shadow: none;
  margin: 0;
  padding: 22px 24px;
}

.auth-provider-grid {
  display: grid;
  gap: 18px;
  grid-template-columns: minmax(220px, 0.34fr) minmax(260px, 1fr) minmax(220px, 0.3fr);
}

.auth-provider-copy span {
  color: #009688;
  display: block;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 8px;
  text-transform: uppercase;
}

.auth-provider-copy h6 {
  color: #263238;
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
}

.auth-provider-copy p {
  color: #607d8b;
  line-height: 1.45;
  margin: 8px 0 0;
}

.auth-provider-picker {
  background: #f8fafb;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  min-width: 0;
  padding: 6px 12px 0;
}

.auth-provider-picker .input-field {
  margin: 0;
}

.auth-provider-actions {
  align-items: stretch;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.auth-provider-action {
  border-radius: 6px;
  box-shadow: none;
  font-weight: 700;
  margin: 0;
  width: 100%;
}

.auth-provider-send {
  background: #00897b !important;
}

.auth-provider-warning {
  grid-column: 2 / -1;
}

.auth-provider-warning p,
.auth-claims-error {
  align-items: center;
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  color: #b71c1c;
  display: flex;
  font-weight: 700;
  gap: 8px;
  margin: 0;
  padding: 10px 12px;
}

.auth-provider-warning i,
.auth-claims-error i {
  font-size: 18px;
}

.auth-provider-workflow :deep(> .card) {
  background: #ffffff !important;
  background-color: #ffffff !important;
  border-radius: 0;
  border-top: 1px solid #c4d3d9;
  box-shadow: none;
  margin: 18px 0 0;
  width: 100%;
}

.auth-diagnosis-row + :deep(> .card) {
  margin-top: 22px;
}

.auth-provider-workflow :deep(> .card > .card-content) {
  padding: 30px 30px 34px !important;
}

.auth-provider-panel .card-title {
  color: #263238 !important;
  font-size: 1.04rem;
  font-weight: 700;
}

.auth-provider-panel :deep(nav),
.auth-provider-panel :deep(.nav-wrapper) {
  background: transparent !important;
  border: 0;
  border-radius: 0;
  box-shadow: none !important;
}

.auth-provider-panel .btn-large {
  border-radius: 6px;
  box-shadow: none;
  font-weight: 700;
}

.auth-diagnosis-row {
  margin: 0;
}

.auth-diagnosis-card {
  align-items: center;
  background: #ffffff !important;
  border: 1px solid #dbe5e9;
  border-radius: 0 0 6px 6px;
  box-sizing: border-box;
  box-shadow: none;
  display: grid;
  gap: 18px;
  grid-template-columns: 52px minmax(0, 1fr) 42px;
  margin: 0;
  min-height: 76px;
  padding: 18px 22px;
  width: 100%;
}

.auth-diagnosis-card > i {
  align-items: center;
  background: #e8f3f2;
  border: 1px solid #cde8e5;
  border-radius: 6px;
  color: #00897b;
  display: inline-flex;
  height: 52px;
  justify-content: center;
  width: 52px;
}

.auth-diagnosis-card span {
  color: #607d8b;
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 6px;
  text-transform: uppercase;
}

.auth-diagnosis-card p {
  color: #263238;
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.45;
  margin: 0;
  overflow-wrap: anywhere;
}

.auth-diagnosis-card a {
  align-self: center;
  align-items: center;
  background: #ffffff;
  border: 1px solid #dbe5e9;
  border-radius: 6px;
  display: inline-flex;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.auth-claims-card {
  border: 1px solid #c4d3d9;
  border-radius: 6px;
  box-shadow: none;
  margin: 24px 0 0;
  width: 100%;
}

.auth-claims-card .card-title {
  color: #263238;
  font-size: 1.05rem;
  font-weight: 650;
}

.auth-claims-card .collection {
  border-color: #e1e8eb;
  border-radius: 6px;
  overflow: hidden;
}

.auth-claims-card .collection-item {
  border-bottom-color: #edf2f4;
  line-height: 1.45;
}

.auth-claims-card .auth-line-item {
  box-sizing: border-box;
  min-height: 54px;
  padding-right: 126px;
  position: relative;
}

.auth-claims-card .auth-line-copy {
  display: block;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: break-word;
}

.auth-claims-card .auth-line-copy b {
  font-weight: 650;
}

.auth-claims-card .line-item-actions {
  align-items: center;
  display: inline-flex;
  gap: 6px;
  justify-content: flex-end;
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  white-space: nowrap;
}

.auth-claims-card .line-item-actions .secondary-content {
  float: none;
  margin-left: 0;
  position: static;
}

.auth-claims-card .line-item-actions a {
  align-items: center;
  border-radius: 50%;
  display: inline-flex;
  height: 30px;
  justify-content: center;
  position: relative;
  width: 30px;
}

.auth-claims-card .line-item-actions a + a::before {
  background: #c4d3d9;
  content: "";
  height: 20px;
  left: -3px;
  position: absolute;
  top: 5px;
  width: 1px;
}

.auth-claims-card .line-item-actions i {
  line-height: 1;
}

.auth-claims-card .btn-large {
  border-radius: 6px;
  box-shadow: none;
  font-weight: 700;
}

.auth-claims-submit-strip {
  align-items: center;
  background: #f5f5f5;
  border: 1px solid #e1e8eb;
  border-radius: 6px;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-top: 16px;
  padding: 14px;
}

.auth-submit-progress {
  border-radius: 0;
  height: 3px;
  margin: 0;
}

.auth-history-shell {
  background: transparent;
  border-top: 0;
  margin-top: 20px;
  padding: 0;
}

.auth-no-encounter {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  color: #607d8b;
  margin-top: 16px;
  padding: 30px;
}

.progress {
  margin-top: -15px;
}

.progress.auth-submit-progress {
  margin: 0;
}

.spacer5 {
  padding-top: 5px;
  padding-bottom: 5px;
}

.spacerTop10 {
  padding-top: 10px;
  padding-bottom: 0px;
}

.edit-black-item:hover .material-icons.black-text,
.edit-black-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

.edit-item:hover .material-icons.grey-text,
.edit-item:hover .material-icons.grey-text::before {
  color: rgb(21, 21, 206) !important;
}

.cancel-item:hover .material-icons.grey-text,
.cancel-item:hover .material-icons.grey-text::before {
  color: rgb(214, 16, 16) !important;
}

@media only screen and (max-width: 992px) {
  .auth-enrollee-dossier {
    padding: 22px !important;
  }

  .auth-enrollee-dossier-grid {
    grid-template-columns: 1fr;
  }

  .auth-enrollee-avatar {
    justify-content: flex-start;
  }

  .auth-provider-summary,
  .auth-diagnosis-row,
  .auth-claims-card {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
  }

  .auth-provider-summary,
  .auth-diagnosis-card {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .auth-provider-grid {
    grid-template-columns: 1fr;
  }

  .auth-provider-warning {
    grid-column: auto;
  }

  .auth-claims-card .auth-line-item {
    padding-right: 20px;
  }

  .auth-claims-card .line-item-actions {
    background: #f8fafb;
    border: 1px solid #dbe5e9;
    border-radius: 999px;
    box-shadow: 0 8px 18px -18px rgba(38, 50, 56, 0.72);
    gap: 10px;
    margin-top: 10px;
    padding: 4px;
    position: static;
    transform: none;
    width: max-content;
  }

  .auth-claims-card .line-item-actions a {
    height: 34px;
    width: 34px;
  }

  .auth-claims-card .line-item-actions a + a::before {
    display: none;
  }

  .auth-claims-submit-strip {
    align-items: stretch;
    flex-direction: column;
  }

  .auth-claims-submit-strip .btn-large {
    width: 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .auth-enrollee-card,
  .auth-provider-workflow,
  .auth-provider-summary,
  .auth-provider-panel,
  .auth-diagnosis-card,
  .auth-claims-card,
  .auth-history-shell,
  .auth-no-encounter {
    animation: none;
  }
}
</style>

<style>
#confirmSubmitEncounterOnly.auth-confirm-modal,
#confirmSubmit.auth-confirm-modal {
  background: #ffffff;
  border: 1px solid #dbe5e9;
  border-radius: 6px;
  box-shadow: 0 24px 56px rgba(25, 35, 42, 0.34);
  left: 50% !important;
  max-width: 720px;
  max-height: 72%;
  position: fixed !important;
  top: 50% !important;
  transform: translate(-50%, -50%) !important;
  width: min(720px, calc(100vw - 32px));
  z-index: 10080 !important;
}

.modal-overlay {
  background: rgba(20, 31, 38, 0.62) !important;
  bottom: 0 !important;
  height: 100vh !important;
  left: 0 !important;
  position: fixed !important;
  right: 0 !important;
  top: 0 !important;
  width: 100vw !important;
  z-index: 10040 !important;
}

.auth-provider-autocomplete-dropdown {
  border: 1px solid #dbe5e9 !important;
  border-radius: 0 0 6px 6px !important;
  box-shadow: 0 18px 34px -24px rgba(38, 50, 56, 0.72) !important;
  max-height: 320px !important;
  overflow-y: auto !important;
  z-index: 10030 !important;
}

.auth-provider-autocomplete-dropdown li > span {
  color: #00897b !important;
  font-size: 1rem;
  line-height: 1.35;
  padding: 14px 18px;
}

.auth-provider-autocomplete-dropdown li.active,
.auth-provider-autocomplete-dropdown li:hover {
  background-color: #f4f7f8 !important;
}

</style>
